import { Routes } from '@angular/router';
import { NotFoundComponent } from './modules/static-page/not-found/not-found.component';
import { AuthGuard } from './modules/auth/guards/auth.guard';
import { AccountTypeGuard } from './modules/auth/guards/account-type.guard';
import { AdminGuard } from './modules/auth/guards/admin.guard';
import { AccountType } from './modules/auth/models/enums/account-type.enum';

export const routes: Routes = [
  {
    path: 'auth',
    loadChildren: () =>
      import('./modules/auth/auth-routing.module').then(m => m.routes),
  },
  {
    path: 'user',
    loadChildren: () =>
      import('./modules/user/user-routing.module').then(m => m.routes),
  },
  {
    path: 'dashboard',
    data: { titleKey: 'layout.topbar.titles.dashboard' },
    loadChildren: () =>
      import('./modules/dashboard/dashboard-routing-module').then(m => m.routes),
  },
  // === Candidate-only routes ===
  {
    path: 'cv',
    canActivate: [AuthGuard, AccountTypeGuard],
    data: { accountTypes: [AccountType.CANDIDATE], titleKey: 'layout.topbar.titles.myCv' },
    loadChildren: () =>
      import('./modules/candidate/cv/cv-routing.module').then(m => m.routes),
  },
  {
    path: 'tasks',
    canActivate: [AuthGuard, AccountTypeGuard],
    data: { accountTypes: [AccountType.CANDIDATE], titleKey: 'layout.topbar.titles.tasks' },
    loadChildren: () =>
      import('./modules/candidate/tasks/tasks-routing.module').then(m => m.routes),
  },
  {
    path: 'browse-offers',
    canActivate: [AuthGuard, AccountTypeGuard],
    data: { accountTypes: [AccountType.CANDIDATE], titleKey: 'layout.topbar.titles.browseOffers' },
    loadChildren: () =>
      import('./modules/candidate/job-offers-browse/job-offers-browse-routing.module').then(m => m.routes),
  },
  // === Company-only routes ===
  {
    path: 'company-profile',
    canActivate: [AuthGuard, AccountTypeGuard],
    data: { accountTypes: [AccountType.COMPANY], titleKey: 'layout.topbar.titles.companyProfile' },
    loadChildren: () =>
      import('./modules/company/company-profile/company-profile-routing.module').then(m => m.routes),
  },
  {
    path: 'candidates',
    canActivate: [AuthGuard, AccountTypeGuard],
    data: { accountTypes: [AccountType.COMPANY], titleKey: 'layout.topbar.titles.candidateSearch' },
    loadChildren: () =>
      import('./modules/company/candidate-search/candidate-search-routing.module').then(m => m.routes),
  },
  {
    path: 'job-offers',
    canActivate: [AuthGuard, AccountTypeGuard],
    data: { accountTypes: [AccountType.COMPANY], titleKey: 'layout.topbar.titles.jobOffers' },
    loadChildren: () =>
      import('./modules/company/job-offers/job-offers-routing.module').then(m => m.routes),
  },
  {
    path: 'recruitment-pipeline',
    canActivate: [AuthGuard, AccountTypeGuard],
    data: { accountTypes: [AccountType.COMPANY], titleKey: 'layout.topbar.titles.recruitmentPipeline' },
    loadChildren: () =>
      import('./modules/company/recruitment-pipeline/recruitment-pipeline-routing.module').then(m => m.routes),
  },
  {
    path: 'applications/inbox',
    canActivate: [AuthGuard, AccountTypeGuard],
    data: { accountTypes: [AccountType.COMPANY], titleKey: 'layout.topbar.titles.applications' },
    loadChildren: () =>
      import('./modules/company/applications-inbox/applications-inbox-routing.module').then(m => m.routes),
  },
  {
    path: 'pipeline-settings',
    canActivate: [AuthGuard, AccountTypeGuard],
    data: { accountTypes: [AccountType.COMPANY], titleKey: 'layout.topbar.titles.pipelineSettings' },
    loadChildren: () =>
      import('./modules/company/pipeline-settings/pipeline-settings-routing.module').then(m => m.routes),
  },
  {
    path: 'favorite-candidates',
    canActivate: [AuthGuard, AccountTypeGuard],
    data: { accountTypes: [AccountType.COMPANY], titleKey: 'layout.topbar.titles.favoriteCandidates' },
    loadChildren: () =>
      import('./modules/company/favorite-candidates/favorite-candidates-routing.module').then(m => m.routes),
  },
  {
    path: 'talent-pool',
    canActivate: [AuthGuard, AccountTypeGuard],
    data: { accountTypes: [AccountType.COMPANY], titleKey: 'layout.topbar.titles.talentPool' },
    loadChildren: () =>
      import('./modules/company/talent-pool/talent-pool-routing.module').then(m => m.routes),
  },
  {
    // Celowo BEZ AccountTypeGuard: kandydat klikający link z maila ma
    // zobaczyć wyjaśnienie (komponent sprawdza typ konta), a nie cichy
    // redirect na /dashboard.
    path: 'team/invitations/accept',
    canActivate: [AuthGuard],
    loadComponent: () =>
      import('./modules/company/company-profile/containers/team-invitation-accept-page/team-invitation-accept-page.component').then(
        c => c.TeamInvitationAcceptPageComponent,
      ),
  },
  // === Shared routes ===
  {
    path: 'access-requests',
    data: { titleKey: 'layout.topbar.titles.accessRequests' },
    loadChildren: () =>
      import('./modules/access-requests/access-requests-routing.module').then(m => m.routes),
  },
  {
    path: 'interviews',
    data: { titleKey: 'layout.topbar.titles.interviews' },
    loadChildren: () =>
      import('./modules/interviews/interviews-routing.module').then(m => m.routes),
  },
  // {
  //   path: 'content-generator',
  //   loadChildren: () =>
  //     import('./modules/content-generator/content-generator-routing.module').then(m => m.routes),
  // },
  {
    path: 'settings',
    data: { titleKey: 'layout.topbar.titles.settings' },
    loadChildren: () =>
      import('./modules/settings/settings-routing.module').then(m => m.routes),
  },
  {
    path: 'consents',
    data: { titleKey: 'layout.topbar.titles.consents' },
    loadChildren: () =>
      import('./modules/consents/consents-routing.module').then(m => m.routes),
  },
  {
    path: 'chat',
    data: { titleKey: 'layout.topbar.titles.chat' },
    loadChildren: () =>
      import('./modules/chat/chat-routing.module').then(m => m.routes),
  },
  {
    path: 'notifications',
    data: { titleKey: 'layout.topbar.titles.notifications' },
    loadChildren: () =>
      import('./modules/notifications/notifications-routing.module').then(m => m.routes),
  },
  {
    path: 'my-applications',
    canActivate: [AuthGuard, AccountTypeGuard],
    data: { accountTypes: [AccountType.CANDIDATE], titleKey: 'layout.topbar.titles.myApplications' },
    loadChildren: () =>
      import('./modules/my-applications/my-applications-routing.module').then(m => m.routes),
  },
  {
    path: 'admin/companies',
    canActivate: [AuthGuard, AdminGuard],
    data: { titleKey: 'admin.companies.title' },
    loadChildren: () =>
      import('./modules/admin/company-moderation/company-moderation-routing.module').then(m => m.routes),
  },
  { path: '', redirectTo: '/dashboard', pathMatch: 'full' },
  { path: '**', component: NotFoundComponent },
];
